/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_BLOCK, TXT_BLOCKS } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addBlockInput,
  deleteBlockInput,
  getBlocksInput,
  getBlockOutput,
  GET_BLOCK,
  updateBlockInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageBlocksTable } from './ManageBlocksTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { block } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_BLOCK,
  DELETE_BLOCK,
  UPDATE_BLOCK
} from '../../../api';
import { ManageBlockEditor } from './ManagerBlockEditor';

export const ManageBlocks = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [blockData, setBlockData] = useState<block[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedBlock, setSelectedBlock] = useState<block | undefined>(undefined);
  const [getBlocks, { loading, data, error, refetch }] = useLazyQuery<getBlockOutput>(GET_BLOCK);
  const [getAllBlocks] = useLazyQuery<getBlockOutput>(GET_BLOCK, {
    onCompleted: (data) => setBlockData(data.blocks.items)
  });

  useEffect(() => {
    getBlocks();
    getAllBlocks();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchBlocks = () => {
    const variables: getBlocksInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getBlocks({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchBlocks();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditBlockEditor = (block: block) => {
    setSelectedBlock(block);
    setOpenEditor(true);
  };

  const openDeleteBlockConfirmation = (block: block) => {
    setSelectedBlock(block);
    setOpenDeleteConfirmation(true);
  };

  const openAddBlockEditor = () => {
    setSelectedBlock(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingBlock = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingBlockError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addBlock, addBlockOperation] = useMutation<block>(ADD_BLOCK, {
    onError: (e) => handleAddingOrUpdatingBlockError(e),
    onCompleted: (result) => afterAddingOrUpdatingBlock()
  });

  const [updateBlock, updateBlockOperation] = useMutation<block>(UPDATE_BLOCK, {
    onError: (e) => handleAddingOrUpdatingBlockError(e),
    onCompleted: (result) => afterAddingOrUpdatingBlock()
  });

  const onEditorSave = (payload: addBlockInput | updateBlockInput) => {
    if (!selectedBlock) {
      addBlock({
        variables: {
          input: payload
        }
      });
    } else {
      updateBlock({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteBlock, deleteBlockOperation] = useMutation<boolean>(DELETE_BLOCK, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.blocks.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteBlockInput = {
      id: selectedBlock!.id!
    };
    deleteBlock({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_BLOCKS))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageBlockEditor
              block={selectedBlock}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedBlock ? addBlockOperation.loading :
                  updateBlockOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteBlockOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedBlock?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-block-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddBlockEditor}>
                        {t(TXT_ADD_BLOCK).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_BLOCKS} data={blockData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageBlocksTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditBlockEditor}
                    onDelete={openDeleteBlockConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.blocks.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
